import { logger, type AudioPosition } from '@tunasong/models'
import { useDelayedInvoke } from '@tunasong/ui-lib'
import React, { useCallback, useEffect, useState } from 'react'
import { useAudioEngine } from '../hooks/audio-engine.js'

export interface PlayerProps {
  /** Name of the player to be shown in the Mixer */
  el: HTMLMediaElement | null
  name?: string
  volume?: number
  /** Automatically stop when another player is started */
  autoStop?: boolean
  // Fraction of the speed the audio is to be played, e.g. 0.5 for half the audio
  playbackRate?: number

  onPlay?(): void
  onPause?(): void
}

export interface MixedPlayer {
  audioProps: Partial<React.DetailedHTMLProps<React.AudioHTMLAttributes<HTMLAudioElement>, HTMLAudioElement>>
  playing: boolean
  duration: number
  position: number
  play(): Promise<void>
  pause(): void
  seek(seconds: number): void
  togglePlay(): void
}

/**
 * The AudioPlayer will play a single audio file. Events will he scheduled on the Timeline.
 * Only one AudioPlayer works at the same time because the Timeline is a singleton connected to
 * the AudioContext.
 */

export const useAudioPlayer = ({ el, onPause, onPlay, playbackRate }: PlayerProps): MixedPlayer => {
  const playing = Boolean(el && el.paused !== true)
  const [pending, setPending] = useState(false)
  const engine = useAudioEngine()

  const schedulePlay = () => {
    if (!el) {
      return
    }
    const playWhenReady = () => {
      logger.debug('Scheduled play running...', { el, playing, pending, readyState: el.readyState })
      play()
    }

    el.addEventListener('canplay', playWhenReady, { once: true })
  }

  // Set the playback rate if provided
  useEffect(() => {
    if (el && playbackRate) {
      logger.debug('Setting playback rate', { playbackRate, el })
      // eslint-disable-next-line react-compiler/react-compiler
      el.playbackRate = playbackRate
    }
  }, [el, playbackRate])

  const play = async () => {
    if (!el || pending) {
      return
    }

    if (el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      logger.debug('Audio not ready to play - scheduling ', { readyState: el.readyState })
      schedulePlay()
      return
    }

    setPending(true)
    // Ensure the audio engine is started
    if (!engine.started) {
      await engine.start()
    }

    if (onPlay) {
      onPlay()
    }

    // el.pause()

    return el
      .play()
      .catch(err => {
        logger.error('Error playing', err)
        throw err
      })
      .finally(() => {
        setPending(false)
      })
  }

  const seek = (seconds: number | AudioPosition) => {
    if (!el) {
      return
    }
    const secs = typeof seconds === 'number' ? seconds : seconds.seconds
    /** We will sync the transport to this when seek is done */

    el.currentTime = secs
  }

  const pause = useCallback(() => {
    if (!el || pending) {
      return
    }

    el.pause()
    if (onPause) {
      onPause()
    }
  }, [el, onPause, pending])

  /** Update position. We debounce to avoid a lot of updates */
  const [position, setPosition] = useState(0)
  const debouncePosition = useDelayedInvoke(100)
  useEffect(() => {
    if (!el) {
      return
    }
    const handleTimeUpdate = () => debouncePosition(() => setPosition(el.currentTime))
    el.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      el.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [debouncePosition, el, playing])

  const togglePlay = async () => {
    if (!el) {
      return
    }
    if (el.paused) {
      await play()
    } else {
      pause()
    }
  }
  const [duration, setDuration] = useState(el?.duration ?? 0)

  /** Update playing status */
  useEffect(() => {
    if (!el) {
      return
    }
    // const handlePause = () => dispatch(features.audio.actions.setPlayer({ isPlaying: false }))
    const handleDuration = (ev: Event) => {
      setDuration((ev.target as HTMLMediaElement).duration)
    }
    // this prevents us from auto-playing the next track
    // el.addEventListener('ended', handlePause)
    el.addEventListener('durationchange', handleDuration)

    return () => {
      // el.removeEventListener('ended', handlePause)
      el.removeEventListener('durationchange', handleDuration)
    }
  }, [el])

  return {
    playing,
    play,
    seek,
    pause,
    togglePlay,
    duration,
    position,
    audioProps: {
      crossOrigin: 'anonymous',
    },
  }
}
